import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaChartBar, FaBuilding, FaMicrochip, FaChevronRight } from "react-icons/fa";
import usePermission from "../../Hooks/usePermission";
import {
  FaDollarSign,
  FaChartLine,
  FaFile,
  FaMapMarkerAlt,
  FaPlug,
  FaClipboardList,
  FaStar,
  FaExchangeAlt,
  FaWrench,
  FaBolt,
  FaFilePdf,
  FaHistory,
} from "react-icons/fa";
import { PageShell, PageHeader, SearchInput } from "../../Components/shared";

const FIRM_REPORTS = [
  {
    title: "Müşteri Bakiye Seviyeleri Raporu",
    description: "Firmaların bakiye bilgisini gösterir",
    path: "/Anasayfa/Rapor/FirmaBakiyeSeviye",
    icon: FaDollarSign,
    color: "from-emerald-400 to-emerald-500",
    bg: "bg-emerald-50",
    iconBg: "bg-emerald-100",
    border: "border-emerald-200",
    permission: "reports.firmBalance",
  },
  {
    title: "Müşteri Eklenenler Raporu",
    description: "2 Tarih arası eklenen firmaları gösterir",
    path: "/Anasayfa/Rapor/FirmaAramızaKatılan",
    icon: FaChartLine,
    color: "from-blue-400 to-blue-500",
    bg: "bg-blue-50",
    iconBg: "bg-blue-100",
    border: "border-blue-200",
    permission: "reports.firmNew",
  },
  {
    title: "Yüklenen Faturalar Raporu",
    description: "2 Tarih arası firmaların fatura hareketlerini gösterir",
    path: "/Anasayfa/Rapor/YüklenenFaturalar",
    icon: FaFile,
    color: "from-purple-400 to-purple-500",
    bg: "bg-purple-50",
    iconBg: "bg-purple-100",
    border: "border-purple-200",
    permission: "reports.firmBilling",
  },
  {
    title: "Müşteri Listesi Konum Merkezli Raporu",
    description: "İl ve ilçe bazında firma raporu",
    path: "/Anasayfa/Rapor/FirmaLokasyonaGöre",
    icon: FaMapMarkerAlt,
    color: "from-orange-400 to-orange-500",
    bg: "bg-orange-50",
    iconBg: "bg-orange-100",
    border: "border-orange-200",
    permission: "reports.firmLocation",
  },
  {
    // Günlük / Aylık / Serbest aralık / Yıllık dört ayrı kart iken tek karta
    // indi; dönem sayfanın içinde seçiliyor. İzinler bölünmüş kaldığı için
    // kart "dördünden herhangi biri" ile görünür, sayfa da dönem listesini
    // izne göre filtreler.
    title: "Müşteri Performans Raporu",
    description: "Tek sayfada gün, ay, tarih aralığı veya yıl seçerek performans PDF'i",
    path: "/Anasayfa/Rapor/MusteriPerformans",
    icon: FaFilePdf,
    color: "from-red-400 to-red-500",
    bg: "bg-red-50",
    iconBg: "bg-red-100",
    border: "border-red-200",
    anyOf: [
      "reports.firmDailyPerformance",
      "reports.firmPerformance",
      "reports.firmRangePerformance",
      "reports.firmsPerformance",
    ],
  },
];

const DEVICE_REPORTS = [
  {
    title: "Cihaz Bağlantı Bilgileri Raporu",
    description: "Belirtilen saattir bağlanmayan cihazları gösterir",
    path: "/Anasayfa/Rapor/CihazBaglantı",
    icon: FaPlug,
    color: "from-red-400 to-red-500",
    bg: "bg-red-50",
    iconBg: "bg-red-100",
    border: "border-red-200",
    permission: "reports.deviceConnection",
  },
  {
    title: "Cihaz Genel Bilgileri Raporu",
    description: "Cihazların genel bilgilerini gösterir",
    path: "/Anasayfa/Rapor/CihazGenel",
    icon: FaClipboardList,
    color: "from-slate-400 to-slate-500",
    bg: "bg-slate-50",
    iconBg: "bg-slate-100",
    border: "border-slate-200",
    permission: "reports.deviceGeneral",
  },
  {
    title: "Cihaz Eklenenler Raporu",
    description: "2 Tarih arası eklenen cihaz özet bilgilerini gösterir",
    path: "/Anasayfa/Rapor/UretilenCihaz",
    icon: FaStar,
    color: "from-amber-400 to-amber-500",
    bg: "bg-amber-50",
    iconBg: "bg-amber-100",
    border: "border-amber-200",
    permission: "reports.deviceNew",
  },
  {
    title: "Cihaz Hareket Raporu",
    description: "2 Tarih arası cihaz üzerinde yapılan işlemleri gösterir",
    path: "/Anasayfa/Rapor/CihazHareket",
    icon: FaExchangeAlt,
    color: "from-cyan-400 to-cyan-500",
    bg: "bg-cyan-50",
    iconBg: "bg-cyan-100",
    border: "border-cyan-200",
    permission: "reports.deviceStatus",
  },
  {
    title: "Cihaz Servis Hareketleri Raporu",
    description: "2 Tarih arası cihazların servis hareketlerini gösterir",
    path: "/Anasayfa/Rapor/CihazServisHareket",
    icon: FaWrench,
    color: "from-violet-400 to-violet-500",
    bg: "bg-violet-50",
    iconBg: "bg-violet-100",
    border: "border-violet-200",
    permission: "reports.deviceServiceStatus",
  },
  {
    title: "Cihaz Performans Raporu",
    description: "2 Tarih arası cihazların performansını gösterir",
    path: "/Anasayfa/Rapor/CihazPerformans",
    icon: FaBolt,
    color: "from-yellow-400 to-yellow-500",
    bg: "bg-yellow-50",
    iconBg: "bg-yellow-100",
    border: "border-yellow-200",
    permission: "reports.devicePerformance",
  },
  {
    title: "Tarihteki Makineler",
    description: "Seçilen günde kaç makinenin nerede olduğunu ve müşterinin makine geçmişini gösterir",
    path: "/Anasayfa/Rapor/TarihtekiMakineler",
    icon: FaHistory,
    color: "from-teal-400 to-teal-500",
    bg: "bg-teal-50",
    iconBg: "bg-teal-100",
    border: "border-teal-200",
    permission: "reports.devicesAtDate",
  },
];

function ReportCard({ report }) {
  const Icon = report.icon;
  return (
    <Link to={report.path} className="group">
      <div className={`${report.bg} ${report.border} border rounded-xl p-3.5 sm:p-4 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5`}>
        <div className="flex items-start gap-3">
          <div className={`p-2.5 ${report.iconBg} rounded-lg shrink-0 group-hover:scale-105 transition-transform duration-200`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-800 mb-1 group-hover:text-gray-900 transition-colors">
              {report.title}
            </h3>
            <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
              {report.description}
            </p>
          </div>
          <FaChevronRight className="w-3 h-3 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
        </div>
      </div>
    </Link>
  );
}

export default function ReportPage() {
  const [search, setSearch] = useState("");
  const { hasPermission, hasAnyPermission } = usePermission();

  // Bir kart ya tek izinle ya da "şunlardan herhangi biri" ile görünür
  // (birleşik Müşteri Performans Raporu dört izinden birine bakar).
  const canSeeReport = (report) =>
    report.anyOf ? hasAnyPermission(...report.anyOf) : hasPermission(report.permission);

  const allReports = useMemo(() => {
    const tagged = [
      ...FIRM_REPORTS.filter(canSeeReport).map((r) => ({ ...r, category: "firm" })),
      ...DEVICE_REPORTS.filter(canSeeReport).map((r) => ({ ...r, category: "device" })),
    ];

    let filtered = tagged;
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (r) => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)
      );
    }

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const firmCount = allReports.filter((r) => r.category === "firm").length;
  const deviceCount = allReports.filter((r) => r.category === "device").length;

  return (
    <PageShell>
      <div className="p-3 sm:p-4 border-b border-gray-100 space-y-3">
        <PageHeader
          icon={FaChartBar}
          iconColor="blue"
          title="Rapor Merkezi"
          subtitle="Tüm raporlara tek yerden erişim"
          stats={[
            { label: "Toplam Rapor", value: allReports.length, icon: FaChartBar, color: "blue" },
          ]}
        />
        <SearchInput
          placeholder="Rapor ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          wrapperClassName="w-full sm:w-56"
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-3 sm:p-4">
        {allReports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <FaChartBar className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-1">Rapor bulunamadı</p>
            <p className="text-xs text-gray-400">Arama kriterlerini değiştirin</p>
          </div>
        ) : (
          <div className="space-y-5">
            {firmCount > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <FaBuilding className="w-3.5 h-3.5 text-blue-500" />
                  <h2 className="text-sm font-bold text-gray-700">Firma Raporları</h2>
                  <span className="text-[10px] bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded-full">{firmCount}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {allReports.filter((r) => r.category === "firm").map((report) => (
                    <ReportCard key={report.path} report={report} />
                  ))}
                </div>
              </section>
            )}

            {deviceCount > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <FaMicrochip className="w-3.5 h-3.5 text-slate-500" />
                  <h2 className="text-sm font-bold text-gray-700">Cihaz Raporları</h2>
                  <span className="text-[10px] bg-slate-100 text-slate-700 font-semibold px-2 py-0.5 rounded-full">{deviceCount}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {allReports.filter((r) => r.category === "device").map((report) => (
                    <ReportCard key={report.path} report={report} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}
